import moment from "moment-timezone";

const DEFAULT_DISPLAY_FORMAT = "DD MMM YYYY, h:mm A";

const TIME_ONLY_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const TIME_ONLY_AMPM_REGEX =
  /^(0?[1-9]|1[0-2]):([0-5]\d)(:\d{2}(\.\d{1,3})?)?\s*([AaPp][Mm])$/;
const ISO_WITHOUT_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;
const SPACE_DATETIME_WITHOUT_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;

const normalizeTimeOnlyTo24h = (timeValue) => {
  const trimmedValue = String(timeValue || "").trim();

  const twentyFourHourMatch = trimmedValue.match(TIME_ONLY_REGEX);
  if (twentyFourHourMatch) {
    return {
      hours: Number(twentyFourHourMatch[1]),
      minutes: Number(twentyFourHourMatch[2]),
      seconds: Number(twentyFourHourMatch[3] || 0),
    };
  }

  const amPmMatch = trimmedValue.match(TIME_ONLY_AMPM_REGEX);
  if (!amPmMatch) return null;

  // Groups: [1]=hour, [2]=min, [3]=optional :ss, [4]=optional fraction, [5]=AM|PM
  const hour12 = Number(amPmMatch[1]);
  const meridiem = String(amPmMatch[5] || "").toLowerCase();
  let hours = hour12 % 12;
  if (meridiem === "pm") hours += 12;

  let seconds = 0;
  if (amPmMatch[3]) {
    const raw = String(amPmMatch[3]).replace(/^:/, "").split(".")[0];
    const s = Number(raw);
    seconds = Number.isFinite(s) ? s : 0;
  }

  return {
    hours,
    minutes: Number(amPmMatch[2]),
    seconds,
  };
};

/**
 * Normalizes API / storage values so moment parses them as UTC (instant),
 * then callers can shift to local for display.
 */
const normalizeUtcInputString = (utcDateTime) => {
  if (
    utcDateTime === null ||
    utcDateTime === undefined ||
    utcDateTime === ""
  ) {
    return null;
  }

  if (utcDateTime instanceof Date || typeof utcDateTime === "number") {
    return utcDateTime;
  }

  if (typeof utcDateTime !== "string") {
    return null;
  }

  const trimmedValue = utcDateTime.trim();
  if (!trimmedValue) return null;

  const normalizedTime = normalizeTimeOnlyTo24h(trimmedValue);

  if (normalizedTime) {
    const h = String(normalizedTime.hours).padStart(2, "0");
    const m = String(normalizedTime.minutes).padStart(2, "0");
    const s = String(normalizedTime.seconds).padStart(2, "0");
    return `1970-01-01T${h}:${m}:${s}Z`;
  }

  if (
    ISO_WITHOUT_TIMEZONE_REGEX.test(trimmedValue) ||
    SPACE_DATETIME_WITHOUT_TIMEZONE_REGEX.test(trimmedValue)
  ) {
    const withT = trimmedValue.includes(" ")
      ? trimmedValue.replace(" ", "T")
      : trimmedValue;
    return `${withT}Z`;
  }

  return trimmedValue;
};

/**
 * Interprets a local wall-clock value in `timezone` and returns an ISO-8601 UTC string (for APIs / storage).
 * For `Date` / epoch ms: converts that instant to UTC (prefer passing a Date built from `new Date('yyyy-mm-ddThh:mm:ss')`
 * so it matches the browser’s local calendar/time — no separate TZ library required).
 * Supports time-only ("17:00"), naive ISO strings (parsed in `timezone`), and strings with offset/Z.
 */
export const convertLocalDateTimeToUTC = (
  localDateTime,
  { timezone = moment.tz.guess() } = {}
) => {
  if (
    localDateTime === null ||
    localDateTime === undefined ||
    localDateTime === ""
  ) {
    return null;
  }

  if (localDateTime instanceof Date || typeof localDateTime === "number") {
    const m = moment.utc(localDateTime);
    return m.isValid() ? m.toISOString() : null;
  }

  if (typeof localDateTime !== "string") {
    return null;
  }

  const trimmedValue = localDateTime.trim();
  if (!trimmedValue) return null;

  const normalizedTime = normalizeTimeOnlyTo24h(trimmedValue);
  if (normalizedTime) {
    const h = String(normalizedTime.hours).padStart(2, "0");
    const m = String(normalizedTime.minutes).padStart(2, "0");
    const s = String(normalizedTime.seconds).padStart(2, "0");
    const today = moment.tz(timezone).format("YYYY-MM-DD");
    const localM = moment.tz(`${today}T${h}:${m}:${s}`, timezone);
    return localM.isValid() ? localM.utc().toISOString() : null;
  }

  if (
    ISO_WITHOUT_TIMEZONE_REGEX.test(trimmedValue) ||
    SPACE_DATETIME_WITHOUT_TIMEZONE_REGEX.test(trimmedValue)
  ) {
    const withT = trimmedValue.includes(" ")
      ? trimmedValue.replace(" ", "T")
      : trimmedValue;
    const localM = moment.tz(withT, timezone);
    return localM.isValid() ? localM.utc().toISOString() : null;
  }

  const parsed = moment.parseZone(trimmedValue);
  if (!parsed.isValid()) return null;
  return parsed.utc().toISOString();
};

/**
 * Parses stored/API UTC and formats for display in `timezone` (default: device local via moment.tz.guess()).
 * Use `formatOptions` + optional `locale` for Intl-style output (used across the app); otherwise `format` (moment).
 */
export const convertUTCToLocalDateTime = (
  utcDateTime,
  {
    timezone = moment.tz.guess(),
    format = DEFAULT_DISPLAY_FORMAT,
    locale,
    formatOptions,
  } = {}
) => {
  if (
    utcDateTime === null ||
    utcDateTime === undefined ||
    utcDateTime === ""
  ) {
    return "—";
  }

  const normalized = normalizeUtcInputString(utcDateTime);
  if (normalized === null) return "—";

  const utcMoment = moment.utc(normalized);

  if (!utcMoment.isValid()) return "—";

  if (formatOptions && typeof formatOptions === "object") {
    const instant = utcMoment.toDate();
    try {
      const intlOpts = {
        ...formatOptions,
        timeZone: timezone,
      };
      // Avoid ambiguous 12h output across locales/browsers when callers set hour12
      if (intlOpts.hour12 === true && intlOpts.hourCycle == null) {
        intlOpts.hourCycle = "h12";
      }
      if (intlOpts.hour12 === false && intlOpts.hourCycle == null) {
        intlOpts.hourCycle = "h23";
      }
      return new Intl.DateTimeFormat(locale || undefined, intlOpts).format(
        instant
      );
    } catch {
      return utcMoment.tz(timezone).format(format);
    }
  }

  return utcMoment.tz(timezone).format(format);
};

const DATE_ONLY_EXACT_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const DEFAULT_CALENDAR_DATE_FORMAT_OPTIONS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
};

/**
 * Formats attendance calendar dates for display.
 * - YYYY-MM-DD only: local calendar day (no UTC midnight shift).
 * - ISO / timestamps: same UTC→local path as convertUTCToLocalDateTime (do not
 *   strip the date prefix — that keeps the UTC day and shows the wrong date).
 */
export const formatCalendarDateForDisplay = (
  dateValue,
  { locale = "en-GB", formatOptions, timezone } = {}
) => {
  if (dateValue === null || dateValue === undefined || dateValue === "") {
    return "—";
  }

  const trimmed = String(dateValue).trim();
  if (!trimmed) return "—";

  const displayFormat = formatOptions || DEFAULT_CALENDAR_DATE_FORMAT_OPTIONS;

  const dateOnlyMatch = trimmed.match(DATE_ONLY_EXACT_REGEX);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    const localDate = new Date(year, month, day, 12, 0, 0);
    return localDate.toLocaleDateString(locale, displayFormat);
  }

  return convertUTCToLocalDateTime(trimmed, {
    locale,
    timezone,
    formatOptions: displayFormat,
  });
};
